import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import WebAppService from '@/services/WebAppService';
import useAuthStore from '@/store/Auth';
import useProjectsStore from '../store/Projects';

/* eslint-disable @typescript-eslint/no-explicit-any */

interface ProjectsContextType {
  projects: any[];
  projectsLoading: boolean;
  refetchProjects: (
    page?: number,
    searchQuery?: string,
    limit?: number
  ) => void;
  organizationLoading: boolean;
}

const ProjectsContext = createContext<ProjectsContextType | null>(null);

export const ProjectsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [organizationLoading] = useState<boolean>(false);
  const { isAuthenticated, organization } = useAuthStore();
  const { updateProjects } = useProjectsStore();
  const [projects, setProjects] = useState<any[]>([]);
  const queryClient = useQueryClient();
  

  const {
    data: projectsData,
    isLoading: projectsLoading,
    error: projectsError,
  } = useQuery({
    queryKey: [
      'projects',
      {
        page: 1,
        searchQuery: '',
        limit: 10,
        organization_id: (organization as any)?.id,
      },
    ],
    queryFn: WebAppService.getAllProjects,
    enabled: isAuthenticated && !!(organization as any)?.id,
  });

  // Sync fetched data to local state and Zustand store
  useEffect(() => {
    if (projectsData) {
      setProjects((projectsData as any)?.data || []);
      updateProjects((projectsData as any)?.data || []);
    }
  }, [projectsData, updateProjects]);

  // Handle query errors
  useEffect(() => {
    if (projectsError) {
      console.error('Error fetching projects:', projectsError);
    }
  }, [projectsError]);

  const orgId = (organization as any)?.id;

  const refetchProjects = useCallback(
    (page: number = 1, searchQuery: string = '', limit: number = 10) => {
      queryClient.invalidateQueries({
        queryKey: [
          'projects',
          {
            page,
            searchQuery,
            limit,
            organization_id: orgId,
          },
        ],
      });
    },
    [queryClient, orgId]
  );

  const contextValue = useMemo(
    () => ({
      projects,
      projectsLoading,
      refetchProjects,
      organizationLoading,
    }),
    [projects, projectsLoading, refetchProjects, organizationLoading]
  );

  return (
    <ProjectsContext.Provider value={contextValue}>
      {children}
    </ProjectsContext.Provider>
  );
};

export const useProjects = (): ProjectsContextType =>
  useContext(ProjectsContext) as ProjectsContextType;
